import type { Dispatch, SetStateAction } from "react";
import { useAdminRequestScope } from "./useAdminRequestScope";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../shared/api/client";
import type { Notify, WorkflowRow } from "./admin-types";

export function useAdminWorkflowsWorkspace({notify}: {notify: Notify}) {
  const scope = useAdminRequestScope(notify);
  const [rows, setRows] = useState<WorkflowRow[]>([]);
  const [composerOpen, setComposerOpenState] = useState(false);
  const [form, setFormState] = useState({
    name: "",
    priority: "100",
    currency: "",
    minimumAmount: "",
    maximumAmount: "",
    category: "",
    department: "",
    steps: ["GENEL_MUDUR"],
  });
  const pending = useRef(false);
  const revision = useRef(0);
  const renderedRevision = revision.current;
  const [saving, setSaving] = useState(false);
  const setForm: Dispatch<SetStateAction<typeof form>> = value => {revision.current++; setFormState(value);};
  const setComposerOpen: Dispatch<SetStateAction<boolean>> = value => {revision.current++; setComposerOpenState(value);};
  const load = useCallback(() => {
    if (!scope.isMounted()) return;
    const request = scope.beginRead();
    void apiRequest<WorkflowRow[]>("/api/approval-workflows").then(value => {
      if (scope.isCurrentRead(request)) setRows(value);
    }).catch((error: unknown) => {
      if (scope.isCurrentRead(request)) scope.notify(error instanceof Error ? error.message : "Onay akışları alınamadı.");
    });
  }, [scope]);
  useEffect(load, [load]);
  const addStep = () => {
    setForm((current) => ({
      ...current,
      steps: [...current.steps, "GENEL_MUDUR"],
    }));
  };
  const createWorkflow = async () => {
    if (!scope.isMounted() || pending.current || renderedRevision !== revision.current) return;
    if (!form.name.trim() || !form.steps.length) {
      scope.notify("Akış adı ve en az bir onay adımı zorunludur.");
      return;
    }
    pending.current = true;
    setSaving(true);
    const submittedRevision = revision.current;
    try {
      await apiRequest<WorkflowRow>("/api/approval-workflows", {
        method: "POST",
        body: JSON.stringify({
          name: form.name,
          priority: Number(form.priority),
          currency: form.currency || undefined,
          minimumAmount: form.minimumAmount
            ? Number(form.minimumAmount)
            : undefined,
          maximumAmount: form.maximumAmount
            ? Number(form.maximumAmount)
            : undefined,
          category: form.category || undefined,
          department: form.department || undefined,
          steps: form.steps.map((role) => ({ role, required: true })),
        }),
      });
      if (!scope.isMounted()) return;
      scope.invalidateReads();
      load();
      if (submittedRevision === revision.current) {
        revision.current++;
        setComposerOpenState(false);
        setFormState({
          name: "",
          priority: "100",
          currency: "",
          minimumAmount: "",
          maximumAmount: "",
          category: "",
          department: "",
          steps: ["GENEL_MUDUR"],
        });
      }
      scope.notify("Onay akışı oluşturuldu.");
    } catch (error) {
      scope.notify(
        error instanceof Error ? error.message : "Onay akışı oluşturulamadı.",
      );
    } finally {
      pending.current = false;
      if (scope.isMounted()) setSaving(false);
    }
  };
  return { saving, rows, composerOpen, setComposerOpen, form, setForm, addStep, createWorkflow };
}
